import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PiArchiveDuotone, PiHouseDuotone, PiSwordDuotone } from 'react-icons/pi';
import { archiveRoom, getRooms } from '../api/roomService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import EmptyState from '../shared/common/EmptyState';
import { ConfirmModal } from '../shared/common/Modal';
import { SkeletonGrid } from '../shared/common/Skeleton';
import { useAuth } from '../app/providers/AuthContext';
import { useDelayedLoading } from '../shared/utils/useDelayedLoading';
import {
  appendChallengePrefillSearch,
  hasChallengePrefill,
  readChallengePrefillSearch,
} from '../shared/utils/challengeCreationFlow';

const OWNER_BADGE_LABEL = 'Владелец';
const ROOM_ARCHIVED_MESSAGE = 'Комната архивирована: новые пари, ставки, участники и инвайты отключены, история сохранена.';

const getCurrentUserId = (user) => user?.id ?? user?.userId;
const buildRoomPagePath = (roomId) => `/rooms/${roomId}`;
const buildRoomChallengeCreatePath = (roomId) => `/rooms/${roomId}/challenges/create`;

const isOwnedByCurrentUser = (ownerId, currentUserId) => (
  ownerId != null && currentUserId != null && String(ownerId) === String(currentUserId)
);

export default function RoomListPage() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [archiveTarget, setArchiveTarget] = useState(null); // { id, name }
  const [archiving, setArchiving] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const currentUserId = getCurrentUserId(user);
  const challengePrefill = useMemo(() => readChallengePrefillSearch(location.search), [location.search]);
  const hasPrefilledChallenge = hasChallengePrefill(challengePrefill);
  const getRoomNavigationPath = (targetRoomId) => {
    if (!hasPrefilledChallenge) return buildRoomPagePath(targetRoomId);
    return appendChallengePrefillSearch(buildRoomChallengeCreatePath(targetRoomId), challengePrefill);
  };

  useEffect(() => {
    document.title = 'BetMate — Комнаты';
    getRooms()
      .then((data) => setRooms(Array.isArray(data) ? data : []))
      .catch(() => setError('Не удалось загрузить комнаты'))
      .finally(() => setLoading(false));
  }, []);

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    setError('');
    setSuccess('');
    try {
      await archiveRoom(archiveTarget.id);
      setRooms((prev) => prev.filter((r) => r.id !== archiveTarget.id));
      setSuccess(ROOM_ARCHIVED_MESSAGE);
      setArchiveTarget(null);
    } catch {
      setError('Не удалось архивировать комнату');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="bm-h2">Комнаты</h1>
          <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
            Управляй своими игровыми комнатами
          </p>
        </div>
        <Button
          variant="primary"
          icon={<PiHouseDuotone size={16} />}
          onClick={() => navigate('/rooms/create')}
        >
          Создать комнату
        </Button>
      </div>

      {hasPrefilledChallenge && (
        <Card variant="glass" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PiSwordDuotone size={22} style={{ color: 'var(--bm-accent-primary)', flexShrink: 0 }} />
            <div>
<div className="bm-font-medium">Событие выбрано для нового пари</div>
              <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 2 }}>
Выбери комнату — форма пари откроется с уже заполненным событием.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
            Отменить выбор
          </Button>
        </Card>
      )}

      {error && (
        <div className="bm-auth-error">{error}</div>
      )}
      {success && (
        <div className="bm-auth-success">{success}</div>
      )}

      {showSkeleton ? (
        <SkeletonGrid count={6} />
      ) : rooms.length === 0 ? (
        <EmptyState
          icon={<PiHouseDuotone size={40} />}
          title="Комнат пока нет"
          hint="Создай первую комнату и пригласи друзей"
          actionLabel="Создать комнату"
          onAction={() => navigate('/rooms/create')}
        />
      ) : (
        <div className="bm-grid">
          {rooms.map((room) => {
            const isOwner = isOwnedByCurrentUser(room.ownerId, currentUserId);

            return (
              <Card
                key={room.id}
                variant="interactive"
                onClick={() => navigate(getRoomNavigationPath(room.id))}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 28 }}>🏠</div>
                    {isOwner && (
                      <Badge status="primary">{OWNER_BADGE_LABEL}</Badge>
                    )}
                  </div>
                  {isOwner && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<PiArchiveDuotone size={14} />}
                      onClick={(event) => {
                        event.stopPropagation();
                        setArchiveTarget(room);
                      }}
                      style={{ color: 'var(--bm-accent-red)', flexShrink: 0 }}
                      title="Архивировать комнату"
                    />
                  )}
                </div>
                <h4 className="bm-h4">{room.name}</h4>
                {room.description && (
                  <p className="bm-text-secondary bm-text-sm bm-truncate">{room.description}</p>
                )}
                {hasPrefilledChallenge && (
                  <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--bm-border)' }}>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<PiSwordDuotone size={14} />}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(getRoomNavigationPath(room.id));
                      }}
                      style={{ width: '100%' }}
                    >
Создать пари
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        loading={archiving}
        title="Архивировать комнату"
message={`Архивировать комнату «${archiveTarget?.name}»? Она исчезнет из активного списка, новые пари, ставки, участники и инвайты будут запрещены, а история останется доступной участникам.`}
      />
    </div>
  );
}
